import React from 'react';

// Classic Lo Shu 3×3 layout: number → [row, col]
const GRID_POSITIONS = {
    4: [0, 0], 9: [0, 1], 2: [0, 2],
    3: [1, 0], 5: [1, 1], 7: [1, 2],
    8: [2, 0], 1: [2, 1], 6: [2, 2],
};

// Full numerology data for each grid number
const NUMBER_DATA = {
    1: {
        theme: 'CAREER',
        traits: 'Career, Success, Job, Business, Communication, Individuality',
        element: 'Water',
        body: 'Kidney, Ears',
        colour: 'Black, Dark Blue',
        planet: 'Sun',
        merit: 'Career',
        direction: 'North',
    },
    2: {
        theme: 'LOVE',
        traits: 'Marriage, Love, Relationship, Sensitivity, Intuition, Mother Element',
        element: 'Earth',
        body: 'Abdomen',
        colour: 'Pink, Red, White',
        planet: 'Moon',
        merit: 'Marriage and Relation',
        direction: 'South-West',
    },
    3: {
        theme: 'HEALTH',
        traits: 'Health, Planning, Imagination, Family, Wisdom, Past Element',
        element: 'Hard Wood',
        body: 'Feet, Knees, Ankle',
        colour: 'Green, Blue',
        planet: 'Jupiter',
        merit: 'Health, Family',
        direction: 'East',
    },
    4: {
        theme: 'WEALTH',
        traits: 'Luck, Money, Discipline, Self-Worth, Power Element',
        element: 'Soft Wood',
        body: 'Thick, Liver',
        colour: 'Purple, Red, Green, Blue, Gold',
        planet: 'Rahu',
        merit: 'Wealth',
        direction: 'South-East',
    },
    5: {
        theme: 'BALANCE',
        traits: 'Balance, Stability, Fortune, Mental, Health, Freedom Element',
        element: 'Earth',
        body: 'Internal Organs',
        colour: 'Yellow, Brown, Orange',
        planet: 'Mercury',
        merit: 'Work Energy',
        direction: 'Center',
    },
    6: {
        theme: 'FRIENDS',
        traits: 'Friends, Travelling, New Beginnings, Spirituality, Father Element',
        element: 'Hard Metal',
        body: 'Head',
        colour: 'Grey, Black, White',
        planet: 'Venus',
        merit: 'Friends',
        direction: 'North-West',
    },
    7: {
        theme: 'CHILDREN',
        traits: 'Children, Creativity, Disappointments, Entertainment, Future Element',
        element: 'Soft Metal',
        body: 'Mouth, Lungs',
        colour: 'White, Silver, Gray, Copper',
        planet: 'Ketu',
        merit: 'Children',
        direction: 'West',
    },
    8: {
        theme: 'WISDOM',
        traits: 'Knowledge, Motivation, Intuition, Organized, Spirituality, Study Element',
        element: 'Earth',
        body: 'Hands, Body Weight',
        colour: 'Blue, Black, Green',
        planet: 'Saturn',
        merit: 'Knowledge',
        direction: 'North-East',
    },
    9: {
        theme: 'FAME',
        traits: 'Prosperity, Humanity, Social, Life, Fame, Reputation Element',
        element: 'Fire',
        body: 'Heart, Blood, Eyes',
        colour: 'Red',
        planet: 'Mars',
        merit: 'Fame',
        direction: 'South',
    },
};

// Reduce a number to single digit 1–9
const toBase = (n) => {
    while (n > 9) n = String(n).split('').reduce((a, d) => a + parseInt(d), 0);
    return n;
};

export default function LoshuGrid({ grid, bhagyank, mulank, dob, kuaNumber }) {
    const cells = Array(3).fill(null).map(() => Array(3).fill(null));
    Object.entries(GRID_POSITIONS).forEach(([num, [r, c]]) => {
        const count = grid[String(num)] ?? grid[parseInt(num)] ?? 0;
        cells[r][c] = { num: parseInt(num), count };
    });

    // Reduce each special number to its single-digit base (1–9) for grid look-up
    const bhagyankCell = bhagyank != null ? toBase(bhagyank) : null;
    const mulankCell = mulank != null ? toBase(mulank) : null;

    // DOB day number — extract day of month and reduce to 1-9
    const dobDay = dob ? new Date(dob).getUTCDate() : null;          // raw day (1-31)
    const dobCell = dobDay != null ? toBase(dobDay) : null;            // reduced cell

    // Kua Number cell
    const kuaCell = kuaNumber != null ? toBase(kuaNumber) : null;

    return (
        <div>
            {/* ── Special Numbers Legend Row ── */}
            <div style={{
                display: 'flex', gap: 10, justifyContent: 'center',
                marginBottom: 14, flexWrap: 'wrap',
            }}>
                {[
                    { label: 'Mulank', value: mulank, bg: '#4f6ef7', text: '#fff', sub: 'Root Number' },
                    { label: 'Bhagyank', value: bhagyank, bg: '#F59E0B', text: '#000', sub: 'Fortune Number' },
                    { label: 'DOB', value: dobDay, bg: '#10b981', text: '#fff', sub: 'Day of Birth' },
                    ...(kuaNumber != null ? [{ label: 'Kua No.', value: kuaNumber, bg: '#22d3ee', text: '#000', sub: 'Feng Shui Number' }] : []),
                ].map(item => (
                    <div key={item.label} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: 'rgba(255,255,255,0.04)',
                        border: `1.5px solid ${item.bg}55`,
                        borderRadius: 10, padding: '6px 14px',
                    }}>
                        <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: item.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 900, color: item.text,
                            WebkitTextFillColor: item.text,
                            WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
                            flexShrink: 0,
                            boxShadow: `0 0 10px ${item.bg}88`,
                        }}>
                            {item.value}
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{item.label}</div>
                            <div style={{ fontSize: 12, color: item.bg, fontWeight: 700 }}>{item.sub}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── 3×3 Grid ── */}
            <div className="loshu-grid-board" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 0,
                border: '2px solid #1e293b',
                borderRadius: 4,
                overflow: 'hidden',
                background: '#0f172a',
            }}>
                {cells.flat().map((cell, i) => {
                    const { num, count } = cell;
                    const present = count > 0;
                    const repeated = count > 1;
                    const isBhagyank = bhagyankCell === num;
                    const isMulank = mulankCell === num;
                    const isDob = dobCell === num;
                    const isKua = kuaCell === num;
                    const data = NUMBER_DATA[num];

                    const isHighlighted = present || isBhagyank || isMulank || isDob || isKua;

                    const bg = '#0f172a';
                    const hoverBg = '#131f32';

                    // Dot colours: first dot = gold, extra repeats = red shades
                    const dotColors = ['#cead80', '#EF4444', '#F97316', '#EAB308', '#10B981', '#8B5CF6'];

                    return (
                        <div key={i} className="loshu-cell-item" style={{
                            background: bg, border: `1.5px solid rgba(206,173,128,0.25)`,
                            padding: '14px 12px 12px', minHeight: 160,
                            display: 'flex', flexDirection: 'column', gap: 0,
                            transition: 'background 0.2s', position: 'relative',
                        }}
                            onMouseEnter={e => { if (e.currentTarget) e.currentTarget.style.background = hoverBg; }}
                            onMouseLeave={e => { if (e.currentTarget) e.currentTarget.style.background = bg; }}
                        >
                            {/* Top: number circle + badges */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                <div className={`loshu-cell-circle${isHighlighted ? ' highlighted' : ''}`} style={{
                                    width: 46, height: 46, borderRadius: '50%',
                                    background: isHighlighted ? 'rgba(206,173,128,0.15)' : '#111827',
                                    border: `2.5px solid ${isHighlighted ? '#cead80' : 'rgba(206,173,128,0.2)'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 20, fontWeight: 900,
                                    color: isHighlighted ? '#cead80' : 'rgba(206,173,128,0.3)',
                                    flexShrink: 0,
                                    boxShadow: isHighlighted ? '0 0 14px rgba(206,173,128,0.3)' : 'none',
                                }}>
                                    {num}
                                </div>

                                {isMulank && <span style={{ fontSize: 9, fontWeight: 800, color: '#0f172a', background: '#cead80', borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap' }}>◆ Mulank</span>}
                                {isBhagyank && <span style={{ fontSize: 9, fontWeight: 800, color: '#0f172a', background: '#cead80', borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap' }}>★ Bhagyank</span>}
                                {isDob && <span style={{ fontSize: 9, fontWeight: 800, color: '#0f172a', background: '#cead80', borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap' }}>⬟ DOB</span>}
                                {isKua && <span style={{ fontSize: 9, fontWeight: 800, color: '#0f172a', background: '#cead80', borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap' }}>☯ Kua</span>}
                            </div>

                            {/* ── Repeat number chips + count (only shown when number appears 2+ times) ── */}
                            {repeated && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                                    {Array.from({ length: Math.min(count, 6) }).map((_, di) => (
                                        <span key={di} style={{
                                            width: 18, height: 18,
                                            borderRadius: 4,
                                            background: dotColors[di] || '#8B5CF6',
                                            boxShadow: `0 0 6px ${dotColors[di] || '#8B5CF6'}80`,
                                            flexShrink: 0,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 10,
                                            fontWeight: 900,
                                            color: '#0f172a',
                                        }}>{num}</span>
                                    ))}
                                    <span style={{
                                        fontSize: 10, fontWeight: 900,
                                        color: '#EF4444',
                                        background: 'rgba(239,68,68,0.15)',
                                        border: '1px solid rgba(239,68,68,0.35)',
                                        borderRadius: 4, padding: '1px 6px',
                                        marginLeft: 2,
                                    }}>
                                        ×{count}
                                    </span>
                                </div>
                            )}

                            {/* Cell content */}
                            {isHighlighted ? (
                                <div style={{ fontSize: 10.5, color: 'rgba(206,173,128,0.7)', lineHeight: 1.65 }}>
                                    {data?.theme && <div style={{ fontSize: 9, fontWeight: 800, color: 'rgba(206,173,128,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>{data.theme}</div>}
                                    <div style={{ color: '#cead80', fontWeight: 600, marginBottom: 3, fontSize: 11 }}>{data.traits}</div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 4, textAlign: 'center' }}>
                                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.9)', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>Lack</span>
                                    {data?.theme && <span style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: 2, textTransform: 'uppercase' }}>{data.theme}</span>}
                                    {data && (
                                        <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, fontStyle: 'italic' }}>{data.traits}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Rajyog & Golden Yog ── */}
            {(() => {
                // Numbers actually in the DOB grid (count > 0 only)
                const dobPresentNums = new Set(
                    Object.entries(grid).filter(([, v]) => (v ?? 0) > 0).map(([k]) => parseInt(k))
                );
                // For RAJYOG planes — also include special numbers so full planes can fire
                const presentNums = new Set(dobPresentNums);
                if (bhagyankCell) presentNums.add(bhagyankCell);
                if (mulankCell) presentNums.add(mulankCell);
                if (dobCell) presentNums.add(dobCell);

                const has = (...nums) => nums.every(n => presentNums.has(n));
                // Golden Yog: ONLY count numbers physically present in DOB digits
                const hasInDob = (...nums) => nums.every(n => dobPresentNums.has(n));

                const RAJYOG = [
                    // ─ Horizontal (rows) ─
                    { nums: [4, 9, 2], name: 'Mind', desc: 'Top row — Intellect & Wisdom', group: 'horizontal' },
                    { nums: [3, 5, 7], name: 'Heart', desc: 'Middle row — Soul & Balance', group: 'horizontal' },
                    { nums: [8, 1, 6], name: 'Practical', desc: 'Bottom row — Action & Strength', group: 'horizontal' },
                    // ─ Vertical (columns) ─
                    { nums: [4, 3, 8], name: 'Vision', desc: 'Left column — Memory & Past', group: 'vertical' },
                    { nums: [9, 5, 1], name: 'Will', desc: 'Center column — Determination', group: 'vertical' },
                    { nums: [2, 7, 6], name: 'Action', desc: 'Right column — Future & Progress', group: 'vertical' },
                    // ─ Diagonal ─
                    { nums: [4, 5, 6], name: 'Golden', desc: 'Main diagonal — Power & Luck', group: 'diagonal' },
                    { nums: [2, 5, 8], name: 'Silver', desc: 'Anti diagonal — Karma & Destiny', group: 'diagonal' },
                ];

                const GOLDEN_YOG = [
                    { nums: [6, 3, 9], name: 'Golden Triangle', desc: 'Venus · Jupiter · Mars — Supreme wealth & fame' },
                    { nums: [1, 4, 7], name: 'Sattvik', desc: 'Spiritual power — purity of thought' },
                ];

                const activeRaj = RAJYOG.filter(y => has(...y.nums));
                const activeGolden = GOLDEN_YOG.filter(y => hasInDob(...y.nums));

                const horizontal = activeRaj.filter(y => y.group === 'horizontal');
                const vertical = activeRaj.filter(y => y.group === 'vertical');
                const diagonal = activeRaj.filter(y => y.group === 'diagonal');

                if (!activeRaj.length && !activeGolden.length) return (
                    <div style={{ marginTop: 16, padding: '10px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 10, textAlign: 'center' }}>
                        <p style={{ fontSize: 12, color: '#475569' }}>No planes or yogs formed in this chart.</p>
                    </div>
                );

                // Helper to render a group of plane cards
                const renderPlaneGroup = (label, items, color) => {
                    if (!items.length) return null;
                    return (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <div style={{ height: 2, flex: 1, background: `linear-gradient(90deg,${color},transparent)` }} />
                                <span style={{ fontSize: 12, fontWeight: 800, color, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</span>
                                <div style={{ height: 2, flex: 1, background: `linear-gradient(90deg,transparent,${color})` }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
                                {items.map(y => (
                                    <div key={y.name} style={{
                                        padding: '10px 14px', borderRadius: 12,
                                        background: `${color}12`,
                                        border: `1.5px solid ${color}55`,
                                        boxShadow: `0 0 14px ${color}18`,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                {y.nums.map(n => (
                                                    <span key={n} style={{
                                                        width: 22, height: 22, borderRadius: '50%',
                                                        background: color, color: '#fff',
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: 11, fontWeight: 900,
                                                    }}>{n}</span>
                                                ))}
                                            </div>
                                            <span style={{ fontSize: 12, fontWeight: 800, color }}>{y.name}</span>
                                        </div>
                                        <p style={{ fontSize: 10.5, color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>{y.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                };

                return (
                    <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* Horizontal Planes */}
                        {renderPlaneGroup('Horizontal Planes', horizontal, '#6366f1')}

                        {/* Vertical Planes */}
                        {renderPlaneGroup('Vertical Planes', vertical, '#0ea5e9')}

                        {/* Diagonal Planes */}
                        {renderPlaneGroup('Diagonal Planes', diagonal, '#a855f7')}

                        {/* Golden Yog */}
                        {activeGolden.length > 0 && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <div style={{ height: 2, flex: 1, background: 'linear-gradient(90deg,#F59E0B,transparent)' }} />
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#F59E0B', letterSpacing: 1, textTransform: 'uppercase' }}>Yog</span>
                                    <div style={{ height: 2, flex: 1, background: 'linear-gradient(90deg,transparent,#F59E0B)' }} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
                                    {activeGolden.map(y => (
                                        <div key={y.name} style={{
                                            padding: '10px 14px', borderRadius: 12,
                                            background: 'rgba(245,158,11,0.07)',
                                            border: '1.5px solid rgba(245,158,11,0.35)',
                                            boxShadow: '0 0 14px rgba(245,158,11,0.12)',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {y.nums.map(n => (
                                                        <span key={n} style={{
                                                            width: 22, height: 22, borderRadius: '50%',
                                                            background: '#F59E0B', color: '#000',
                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 11, fontWeight: 900,
                                                        }}>{n}</span>
                                                    ))}
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 800, color: '#fcd34d' }}>{y.name}</span>
                                            </div>
                                            <p style={{ fontSize: 10.5, color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>{y.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#475569' }} />
                    Present in DOB
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444' }} />
                    Repeated
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1e293b' }} />
                    Lack
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#cead80' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4f6ef7' }} />
                    Mulank (Root)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#F59E0B' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B' }} />
                    Bhagyank (Fortune)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#10b981' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} />
                    DOB (Day of Birth)
                </div>
                {kuaNumber != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#22d3ee' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22d3ee' }} />
                        Kua No. (Feng Shui)
                    </div>
                )}
            </div>
        </div>
    );
}
